import { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle,
  Delete,
  Info,
  Package,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { safeAreaBottom } from '../lib/safe-area'
import { resultingTotal } from '../lib/capture-flow'
import {
  fractionalEntryValue,
  pressFractionalKey,
} from '../lib/fractional-keypad'
import type { CaptureFailureKind } from '../lib/capture-flow'
import type { FractionalKey } from '../lib/fractional-keypad'
import type { ReactNode } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { formatStockQuantity } from '@/shared/lib/stock-quantity'

export interface QuantitySheetTarget {
  sku: string
  name: string
  imageUrl: string | null
  /** Already-captured total for this SKU in the session, if any — drives the add/replace choice. */
  previousQty: number | null
  /**
   * The location's expected quantity, or null to show none. Null covers both "not applicable"
   * and a blind location, whose assortment payload carries no expected quantities at all — the
   * client cannot hide what it never received.
   */
  expectedQty?: number | null
  /**
   * "Teilmengen zählen" (CHG-2026-09-stock-usage-and-price-twins, D9): the pad gets a comma key,
   * a "Leeren" button and ¼ ½ ¾ quick keys, and the entry may carry up to 3 decimals. Absent or
   * false keeps the whole-number pad exactly as it has always been.
   */
  fractional?: boolean
  /** The base unit as shown next to a partial amount ("Kiste", "Stk"). */
  unit?: string
}

interface QuantitySheetProps {
  target: QuantitySheetTarget | null
  onClose: () => void
  /** `mode` is what the entered quantity means: accumulate onto the total, or become it. */
  onSubmit: (qty: number, mode: 'add' | 'replace') => void
  /**
   * What the entered quantity means when no choice is offered — the review screen's recount always
   * replaces. Where `allowModeSwitch` is on, the sheet opens on 'add' regardless: finding more of
   * an article in a second location is the common case, and it is the recoverable one, because the
   * running total stays visible while a replace discards it. A typed 0 overrides the choice and
   * always replaces, whichever mode is active.
   */
  mode?: 'add' | 'replace'
  /**
   * Offers the explicit "+ dazu / ersetzen" choice whenever a quantity already exists. The review
   * screen leaves this off: a recount there always replaces.
   */
  allowModeSwitch?: boolean
  /** When given, a destructive "Erfassung entfernen" action is offered (wrong article scanned). */
  onDelete?: () => void
  /** A capture is on the wire: confirm is blocked so a slow request cannot be double-submitted. */
  pending?: boolean
  /** The last attempt failed — the sheet stays open with the typed quantity and says why. */
  failure?: CaptureFailureKind | null
  /** Repeats the failed attempt under its original `captureId`; only offered for a blip. */
  onResend?: () => void
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
const MAX_DIGITS = 5

/**
 * Quantity entry after a scan/search hit, a recount, or a correction.
 *
 * Digits come from the pad below, not from the OS keyboard: the display is a plain element and
 * nothing is focused when the sheet opens. A focused input used to pull up the mobile keyboard,
 * which resizes `visualViewport`; vaul then repositions the drawer against a viewport that is still
 * animating and pushed the sheet off-screen, leaving the counter on a blank screen until they
 * confirmed blind (round-4 bug report). No focus, no keyboard, no reposition. A hardware keyboard
 * still works via the window-level key handler below, for the desktop review screen.
 *
 * The expected quantity is shown here when the location is configured for it
 * (CHG-2026-09-inventory-mobile-ux reverses the blind-counting default). It is informational: the
 * variance is computed server-side from the snapshot taken at the first capture, so this number
 * cannot influence any correction — but it does anchor the counter, which is why the flag exists.
 */
export function QuantitySheet({
  target,
  onClose,
  onSubmit,
  mode = 'add',
  allowModeSwitch = false,
  onDelete,
  pending = false,
  failure = null,
  onResend,
}: QuantitySheetProps) {
  const { t } = useTranslation('inventory-count')
  const [qty, setQty] = useState('')
  const [activeMode, setActiveMode] = useState<'add' | 'replace'>(mode)

  const open = target != null
  const fractional = target?.fractional === true
  const unit = target?.unit ?? null
  const previousQty = target?.previousQty ?? null
  const expectedQty = target?.expectedQty ?? null
  // Nothing accumulates onto a total that does not exist yet, so the choice is only ever offered
  // for an article that already carries one.
  const showModeSwitch = allowModeSwitch && previousQty != null

  useEffect(() => {
    if (target) {
      // Where both meanings are on offer, the sheet opens on 'add'.
      setActiveMode(allowModeSwitch ? 'add' : mode)
      // Always an empty pad, in either mode: the counter types the number they just counted, and
      // a prefilled total has to be cleared before it can be replaced — the same reason the
      // default "1" was dropped in round 4.
      setQty('')
    }
  }, [target, mode, allowModeSwitch])

  // The whole-number pad parses exactly as it always did; a partial-amount entry reads the de-AT
  // comma (a trailing "2," counts as 2).
  const fractionalValue = fractional ? fractionalEntryValue(qty) : null
  const parsed = fractional
    ? (fractionalValue ?? Number.NaN)
    : Number.parseInt(qty, 10)
  const valid = fractional
    ? fractionalValue != null
    : qty.trim() !== '' && Number.isFinite(parsed) && parsed >= 0
  /**
   * A typed 0 always replaces the total, whatever the switch says: in front of an empty shelf the
   * counter's answer is "none", and adding zero to a total would be a request that changes nothing.
   * The server agrees — it accepts 0 only as a replacement — so this is also what keeps a first
   * capture of 0 from being rejected, which used to leave the confirm button grey with no
   * explanation (CHG-2026-09-inventory-count-review-ux).
   */
  const isZero = valid && parsed === 0
  const effectiveMode: 'add' | 'replace' = isZero ? 'replace' : activeMode

  // The whole-number pad, exactly as it has always been.
  const pressWhole = useCallback((key: string) => {
    setQty((current) => {
      if (key === 'clear') return ''
      if (key === 'backspace') return current.slice(0, -1)
      if (current.length >= MAX_DIGITS) return current
      // A leading zero carries no meaning here and would make "0" unreadable as "not present".
      if (current === '0') return key
      return current + key
    })
  }, [])

  const press = useCallback(
    (key: string) => {
      if (fractional) {
        setQty((current) => pressFractionalKey(current, key as FractionalKey))
      } else {
        pressWhole(key)
      }
    },
    [fractional, pressWhole],
  )

  const submit = useCallback(() => {
    if (valid && !pending) onSubmit(parsed, effectiveMode)
  }, [valid, pending, parsed, effectiveMode, onSubmit])

  // Hardware keyboard support (desktop review screen): the pad is the only input, so keys are
  // handled at the window while the sheet is open rather than on a focused field.
  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key >= '0' && e.key <= '9') {
        press(e.key)
      } else if (fractional && (e.key === ',' || e.key === '.')) {
        // A partial amount: either decimal key of a hardware keyboard types the comma.
        press(',')
      } else if (e.key === 'Backspace') {
        press('backspace')
      } else if (e.key === 'Enter') {
        submit()
      } else {
        return
      }
      e.preventDefault()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, press, submit, fractional])

  // A closed session or an applied correction cannot be sent again at all: the only way out of
  // those two is closing the sheet.
  const blocked =
    failure === 'session_closed' ||
    failure === 'already_resolved' ||
    failure === 'no_stock' ||
    failure === 'whole_required' ||
    failure === 'invalid_quantity'
  const total = resultingTotal(previousQty, parsed, effectiveMode)
  const showTotal = valid && effectiveMode === 'add' && previousQty != null

  // Whole-number articles show the bare numbers, as always; a partial amount reads "1,85 Kiste".
  const shown = (value: number): number | string =>
    fractional
      ? [formatStockQuantity(value), unit].filter(Boolean).join(' ')
      : value

  // "Erwartet 22 · bereits gezählt 10", either half alone, or nothing at all — a blind location
  // on a first capture has neither number to show.
  let infoLabel: string | null = null
  if (expectedQty != null && previousQty != null) {
    infoLabel = t('quantitySheet.infoExpectedAndCounted', {
      expected: shown(expectedQty),
      counted: shown(previousQty),
    })
  } else if (expectedQty != null) {
    infoLabel = t('quantitySheet.infoExpected', {
      expected: shown(expectedQty),
    })
  } else if (previousQty != null) {
    infoLabel = t('quantitySheet.infoCounted', { counted: shown(previousQty) })
  }

  let failureLabel: string | null = null
  if (failure === 'session_closed') {
    failureLabel = t('quantitySheet.failureClosed')
  } else if (failure === 'already_resolved') {
    failureLabel = t('quantitySheet.failureResolved')
  } else if (failure === 'connection') {
    failureLabel = t('quantitySheet.failureConnection')
  } else if (failure === 'no_stock') {
    failureLabel = t('quantitySheet.failureNoStock')
  } else if (failure === 'whole_required') {
    failureLabel = t('quantitySheet.failureWholeRequired')
  } else if (failure === 'invalid_quantity') {
    failureLabel = t('quantitySheet.failureInvalidQuantity')
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && onClose()}
      repositionInputs={false}
    >
      <DrawerContent className="max-h-[95svh]">
        <DrawerHeader className="pb-2">
          {/*
            The scan path is where confirming the right article matters most — the counter never
            typed its name — and `resolve` already returns the image URL, which this sheet used to
            drop on the floor. A neutral package icon stands in wherever there is no image (search
            hits, corrections, unknown codes).
          */}
          <div className="flex items-center gap-3 text-left">
            {target?.imageUrl != null ? (
              <img
                src={target.imageUrl}
                alt={target.name}
                className="size-14 shrink-0 rounded-md border border-border bg-muted object-cover"
              />
            ) : (
              <div className="flex size-14 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
                <Package
                  className="h-6 w-6 text-muted-foreground"
                  aria-hidden
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <DrawerTitle className="truncate">{target?.name}</DrawerTitle>
              <DrawerDescription className="truncate">
                {target?.sku}
              </DrawerDescription>
            </div>
          </div>
        </DrawerHeader>
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto px-4 pb-2">
          {/* Expected and already-counted up front, not as a hint after something was typed. */}
          {infoLabel != null && (
            <p className="rounded-md bg-muted px-3 py-2 text-center text-sm tabular-nums text-foreground">
              {infoLabel}
            </p>
          )}

          {showModeSwitch && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={effectiveMode === 'add' ? 'default' : 'outline'}
                className="h-12"
                aria-pressed={effectiveMode === 'add'}
                disabled={isZero}
                onClick={() => setActiveMode('add')}
              >
                {t('quantitySheet.modeAdd')}
              </Button>
              <Button
                variant={effectiveMode === 'replace' ? 'default' : 'outline'}
                className="h-12"
                aria-pressed={effectiveMode === 'replace'}
                onClick={() => setActiveMode('replace')}
              >
                {t('quantitySheet.modeReplace')}
              </Button>
            </div>
          )}

          {fractional ? (
            <div className="flex flex-col gap-1">
              <div
                aria-live="polite"
                aria-label={t('quantitySheet.placeholder')}
                className={cn(
                  'flex h-14 items-center justify-center gap-2 rounded-md border border-input bg-background text-3xl font-semibold tabular-nums',
                  qty === '' && 'text-muted-foreground',
                )}
              >
                <span>{qty === '' ? t('quantitySheet.placeholder') : qty}</span>
                {unit && qty !== '' && (
                  <span className="text-lg font-medium text-muted-foreground">
                    {unit}
                  </span>
                )}
              </div>
              <p className="text-center text-xs text-muted-foreground">
                {t('quantitySheet.decimalHint')}
              </p>
            </div>
          ) : (
            <div
              aria-live="polite"
              aria-label={t('quantitySheet.placeholder')}
              className={cn(
                'flex h-14 items-center justify-center rounded-md border border-input bg-background text-3xl font-semibold tabular-nums',
                qty === '' && 'text-muted-foreground',
              )}
            >
              {qty === '' ? t('quantitySheet.placeholder') : qty}
            </div>
          )}
          {isZero ? (
            <p className="flex items-start justify-center gap-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{t('quantitySheet.zeroHint')}</span>
            </p>
          ) : (
            effectiveMode === 'replace' && (
              <p className="text-center text-sm text-muted-foreground">
                {t('quantitySheet.replaceHint')}
              </p>
            )
          )}

          <div className="grid grid-cols-3 gap-2">
            {KEYS.map((key) => (
              <PadButton key={key} onClick={() => press(key)}>
                {key}
              </PadButton>
            ))}
            {fractional ? (
              // The comma takes the place of "C"; clearing moves to "Leeren" below.
              <PadButton
                onClick={() => press(',')}
                label={t('quantitySheet.comma')}
              >
                ,
              </PadButton>
            ) : (
              <PadButton
                onClick={() => press('clear')}
                label={t('quantitySheet.clear')}
              >
                <span className="text-lg font-medium">
                  {t('quantitySheet.clearShort')}
                </span>
              </PadButton>
            )}
            <PadButton onClick={() => press('0')}>0</PadButton>
            <PadButton
              onClick={() => press('backspace')}
              label={t('quantitySheet.backspace')}
            >
              <Delete className="h-6 w-6" />
            </PadButton>
          </div>
          {fractional && (
            <div className="grid grid-cols-4 gap-2">
              <PadButton
                onClick={() => press('quarter')}
                label={t('quantitySheet.quarterLabel')}
              >
                ¼
              </PadButton>
              <PadButton
                onClick={() => press('half')}
                label={t('quantitySheet.halfLabel')}
              >
                ½
              </PadButton>
              <PadButton
                onClick={() => press('threeQuarters')}
                label={t('quantitySheet.threeQuartersLabel')}
              >
                ¾
              </PadButton>
              <Button
                type="button"
                variant="outline"
                onClick={() => press('clear')}
                className="h-12 text-base font-medium"
              >
                {t('quantitySheet.empty')}
              </Button>
            </div>
          )}
        </div>
        <DrawerFooter className="pt-2" style={safeAreaBottom('1rem')}>
          {/*
            A failed capture keeps its quantity on screen: nothing is discarded, and the resend
            repeats the same captureId, so it cannot count twice. A closed session or an already
            applied correction offer no resend — neither could ever succeed.
          */}
          {failure != null && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
            >
              <p className="flex items-start gap-2">
                <AlertTriangle
                  className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                  aria-hidden
                />
                <span>{failureLabel}</span>
              </p>
              {failure === 'connection' && onResend && (
                <Button
                  variant="outline"
                  className="h-11"
                  disabled={pending}
                  onClick={onResend}
                >
                  <RefreshCw className="h-4 w-4" />
                  {t('quantitySheet.resend')}
                </Button>
              )}
            </div>
          )}
          {!blocked && (
            <Button
              size="lg"
              className="h-12"
              disabled={!valid || pending}
              onClick={submit}
            >
              {/* aria-hidden so the spinner does not rename the button it sits in. */}
              {pending && <Spinner className="size-4" aria-hidden />}
              {showTotal
                ? t('quantitySheet.confirmTotal', { total: shown(total) })
                : t('quantitySheet.confirm')}
            </Button>
          )}
          <div
            className={cn('grid gap-2', onDelete && !blocked && 'grid-cols-2')}
          >
            {onDelete && !blocked && (
              <Button
                variant="outline"
                size="lg"
                className="h-12 text-destructive"
                disabled={pending}
                onClick={onDelete}
              >
                <Trash2 className="h-4 w-4" />
                {t('quantitySheet.remove')}
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              className="h-12"
              onClick={onClose}
            >
              {blocked ? t('quantitySheet.close') : t('quantitySheet.cancel')}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

function PadButton({
  children,
  onClick,
  label,
}: {
  children: ReactNode
  onClick: () => void
  label?: string
}) {
  return (
    <Button
      type="button"
      variant="outline"
      aria-label={label}
      onClick={onClick}
      className="h-12 text-2xl font-semibold tabular-nums"
    >
      {children}
    </Button>
  )
}
